import React, { useEffect, useMemo, useState } from "react";
import { Phone, UserRound, X, ZoomIn } from "lucide-react";
import { ICON_STROKE } from "../../ui/icon";
import { toTitleCase } from "../../../utils/titleCase";

const Fact = ({ label, value, muted, variant, highlight }) => (
  <div
    className={[
      "pub-hero__fact",
      ...(variant ? variant.split(" ").map((v) => `pub-hero__fact--${v}`) : []),
      ...(highlight ? ["pub-hero__fact--highlight"] : []),
    ].join(" ")}
  >
    <dt>{label}</dt>
    <dd className={muted ? "pub-hero__fact-value--muted" : undefined}>{value}</dd>
  </div>
);

function isEmptyMedical(value, notAvailable) {
  const v = (value || "").trim();
  if (!v || v === notAvailable) return true;
  return /^(no active medications|not available|none|n\/a|—|-)$/i.test(v);
}

const PublicEmergencyHero = ({
  name,
  photo,
  age,
  gender,
  address,
  bloodGroup,
  allergies,
  conditions,
  medications,
  primaryContact,
  onCallContact,
  showMedical = false,
  t,
}) => {
  const [photoOpen, setPhotoOpen] = useState(false);
  const displayName = name || t("public.medicalInfoTitle");
  const photoAlt = t("public.ownerPhotoAlt").replace("{{name}}", displayName);
  const notAvailable = t("public.notAvailable");
  const has = (value) => value && value !== notAvailable;

  const medicalHighlights = useMemo(() => {
    if (!showMedical) return [];
    const rows = [];
    if (!isEmptyMedical(allergies, notAvailable)) {
      rows.push({
        id: "allergies",
        label: t("public.allergiesLabel"),
        value: allergies,
        highlight: true,
      });
    }
    if (!isEmptyMedical(conditions, notAvailable)) {
      rows.push({ id: "conditions", label: t("public.conditionsLabel"), value: conditions });
    }
    if (!isEmptyMedical(medications, notAvailable)) {
      rows.push({ id: "medications", label: t("public.activeMedications"), value: medications });
    }
    return rows;
  }, [showMedical, allergies, conditions, medications, notAvailable, t]);

  useEffect(() => {
    if (!photoOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPhotoOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photoOpen]);

  const verifyText = photo ? t("public.verifyPhotoHint") : t("public.verifyNoPhotoHint");

  return (
    <section className="pub-hero" aria-label="Person overview">
      <div className="pub-hero__main">
        <div className="pub-hero__photo-col">
          <p className="pub-hero__verify pub-hero__verify--in-col">{verifyText}</p>
          {photo ? (
            <button
              type="button"
              className="pub-hero__photo-btn pub-hero__photo-btn--aligned"
              onClick={() => setPhotoOpen(true)}
              aria-label={t("public.tapToEnlarge")}
            >
              <img src={photo} alt={photoAlt} className="pub-hero__photo" />
              <span className="pub-hero__zoom" aria-hidden>
                <ZoomIn size={14} strokeWidth={ICON_STROKE} />
              </span>
            </button>
          ) : (
            <div className="pub-hero__photo pub-hero__photo--placeholder pub-hero__photo--aligned" aria-hidden>
              <UserRound size={40} strokeWidth={1.5} />
              <span>{t("public.noPhoto")}</span>
            </div>
          )}
        </div>

        <div className="pub-hero__info">
          <h1 className="pub-hero__name">{displayName}</h1>
          <dl className="pub-hero__identity">
            <Fact label={t("public.fieldAge")} value={age || notAvailable} muted={!age} />
            <Fact label={t("public.fieldGender")} value={gender || notAvailable} muted={!gender} />
            <Fact
              label={t("public.fieldAddress")}
              value={has(address) ? address : notAvailable}
              muted={!has(address)}
              variant="wide"
            />
            <Fact
              label={t("public.bloodGroupLabel")}
              value={has(bloodGroup) ? bloodGroup : notAvailable}
              muted={!has(bloodGroup)}
              variant={has(bloodGroup) ? "blood wide" : "wide"}
            />
          </dl>
          {primaryContact ? (
            <button
              type="button"
              className="pub-hero__call-btn pub-hero__call-btn--block"
              onClick={() => onCallContact?.(primaryContact)}
              aria-label={`${t("public.callPrimary")} — ${toTitleCase(primaryContact.name)}`}
            >
              <Phone size={16} strokeWidth={ICON_STROKE} aria-hidden />
              <span>
                {t("public.callPrimary")}
                {primaryContact.name ? ` (${toTitleCase(primaryContact.name)})` : ""}
              </span>
            </button>
          ) : (
            <span className="pub-hero__contact-name">{t("public.noPrimaryContactShort")}</span>
          )}
        </div>
      </div>

      {medicalHighlights.length > 0 && (
        <div className="pub-hero__critical pub-hero__critical--bystander">
          <p className="pub-hero__critical-heading">{t("public.bystanderMedicalHeading")}</p>
          <dl className="pub-hero__critical-list">
            {medicalHighlights.map((row) => (
              <Fact
                key={row.id}
                label={row.label}
                value={row.value}
                highlight={row.highlight}
              />
            ))}
          </dl>
        </div>
      )}

      {photoOpen && (
        <div
          className="pub-photo-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={photoAlt}
          onClick={() => setPhotoOpen(false)}
        >
          <button
            type="button"
            className="pub-photo-viewer__close"
            onClick={() => setPhotoOpen(false)}
            aria-label={t("public.closePhoto")}
          >
            <X size={24} />
          </button>
          <img src={photo} alt={photoAlt} className="pub-photo-viewer__img" />
          <p className="pub-photo-viewer__name">{displayName}</p>
        </div>
      )}
    </section>
  );
};

export default PublicEmergencyHero;
